import { Trans, useTranslation } from 'next-i18next';
import type { MouseEvent } from 'react';

import { V1A_IMG, V1A_NAMESPACE } from '../constants';
import { useV1aTrial } from '../trial-context';

const TICKS = ['no_card', 'full_access'] as const;

export function Close() {
  const { t } = useTranslation(V1A_NAMESPACE);
  const { openTrial } = useV1aTrial();

  const handleCta = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    openTrial();
  };

  return (
    <section className='v1-close relative isolate overflow-hidden py-[clamp(3.5rem,2rem_+_6vw,7rem)]' aria-labelledby='v1-close-title'>
      <div className='v1-close__bg' aria-hidden='true'>
        <picture>
          <source type='image/avif' srcSet={`${V1A_IMG}/close-bg-960.avif 960w, ${V1A_IMG}/close-bg-1440.avif 1440w, ${V1A_IMG}/close-bg-1920.avif 1920w`} sizes='100vw' />
          <img src={`${V1A_IMG}/close-bg-1440.webp`} srcSet={`${V1A_IMG}/close-bg-960.webp 960w, ${V1A_IMG}/close-bg-1440.webp 1440w, ${V1A_IMG}/close-bg-1920.webp 1920w`} sizes='100vw' width='1920' height='1080' alt='' loading='lazy' decoding='async' />
        </picture>
        <span className='v1-close__scrim' />
        <span className='v1-close__bloom' />
      </div>
      <div className='v1-close__inner relative mx-auto w-full max-w-[90rem] px-[var(--gutter)] text-center'>
        <p className='v1-eyebrow v1-eyebrow--center m-0 inline-flex items-center justify-center gap-2 text-[length:var(--t-micro)] font-semibold uppercase leading-[1.3] tracking-[.16em] text-[#9AA3B5]' data-reveal>
          <span className='v1-eyebrow__dot size-1.5 flex-none rounded-full bg-[#93FF18] shadow-[0_0_10px_rgb(147_255_24/.8)]' aria-hidden='true' />
          {t('close.eyebrow')}
        </p>
        <h2 className='v1-close__title mt-3.5 text-balance text-[length:clamp(2rem,1.1rem_+_3.2vw,3.75rem)] font-bold leading-[1.02] tracking-[-.035em] text-[#FFFFFF]' id='v1-close-title' data-reveal>
          <Trans t={t} i18nKey='close.title' components={{ hl: <span className='v1-hl !text-[#93FF18] [text-shadow:0_0_22px_rgb(147_255_24/.3)] forced-colors:!text-[LinkText]' /> }} />
        </h2>
        <p className='v1-close__lead mx-auto mt-4 max-w-2xl hyphens-auto text-pretty text-[length:var(--t-lead)] leading-[1.55] text-[#CBD5E1]' data-reveal>
          {t('close.lead')}
        </p>
        <p className='v1-close__cta mt-[clamp(1.5rem,1rem_+_1.5vw,2.25rem)]' data-reveal>
          <a className='btn btn-primary btn--lg np-cta !gap-2 max-[480px]:w-full max-[480px]:!px-4 max-[480px]:!text-[length:.875rem]' href='#cadastro' onClick={handleCta}>
            <span>
              {t('close.cta_lead')}{' '}
              <span className='np-cta__end'>
                {t('close.cta_tail')}
                <svg viewBox='0 0 24 24' width='20' height='20' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false' className='rtl:-scale-x-100'>
                  <path d='M5 12h14' />
                  <path d='m13 6 6 6-6 6' />
                </svg>
              </span>
            </span>
          </a>
        </p>
        {/* role=list keeps list semantics that list-style:none removes in Safari/VoiceOver */}
        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
        <ul className='v1-close__ticks mt-[1.125rem] flex list-none flex-wrap justify-center gap-x-5 gap-y-2 !p-0 text-[length:var(--t-micro)] text-[#9AA3B5] [&_li]:inline-flex [&_li]:items-center [&_li]:gap-[.4375rem] [&_svg]:flex-none [&_svg]:text-[#93FF18]' role='list' data-reveal>
          {TICKS.map((tick) => (
            <li key={tick}>
              <svg viewBox='0 0 24 24' width='18' height='18' fill='none' stroke='currentColor' strokeWidth='1.9' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
                <circle cx='12' cy='12' r='9' />
                <path d='m8.4 12.3 2.4 2.4 4.8-5' />
              </svg>
              {t(`close.ticks.${tick}`)}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
